/* eslint-disable react/no-children-prop */
import React, { useContext } from "react";
import PropTypes from "prop-types";
import PassengersCounterDisplay from "app/pages/.shared/PassengersCounterDisplay";
import { FormattedMessage, useIntl } from "react-intl";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import get from "lodash/get";
import TravelDatesContainer from "app/pages/.shared/TravelDates/TravelDatesContainer";
import IconCalendar from "app/pages/.shared/IconCalendar";
import IconNegoBed from "app/pages/.shared/IconNegoBed";
import IconNegoDinner from "app/pages/.shared/IconNegoDinner";
import IconNegoPlane from "app/pages/.shared/IconNegoPlane";
import { getFlightNegotiatedLabelKey } from "app/utils/smartDPUtils";
import IconNegoTransfer from "app/pages/.shared/IconNegoTransfer";
import TripAdvisor from "app/pages/FicheProduit/components/TripAdvisor/TripAdvisor";
import BookingBannerPlaceholderContainer from "app/pages/Booking/BookingBannerPlaceholder/BookingBannerPlaceholderContainer";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";
import Collapse from "rc-collapse";
import MyFlightDetail from "app/pages/Booking/MyFlightDetail";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import AppGlobalsContext from "app/AppGlobalsContext";
import OccupanciesDisplayLabel from "app/pages/.shared/OccupanciesDisplayLabel/OccupanciesDisplayLabel";
import NegociatedItem from "app/pages/FicheProduit/NegociatedList/NegociatedItem";
import TravelDurationDatesContainer from "app/pages/.shared/TravelDurationDates/TravelDurationDatesContainer";
import "./HolidaySummary.scss";
import { NEGOCIATED_TYPES } from "app/constants";
import IconLocation from "app/pages/.shared/IconLocation";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import Timer from "app/pages/.shared/Timer";
import SpecialOfferBadge from "app/pages/.shared/SpecialOffer/SpecialOfferBadge/SpecialOfferBadge";

const IMG_SIZES_DESKTOP = {
	xlarge: { height: 128, width: 340 },
	large: { height: 128, width: 340 },
};

const IMG_SIZES_MOBILE = {
	medium: { width: 800 },
	small: { width: 800 },
};

const NEGOS_TO_EXCLUDE = [
	NEGOCIATED_TYPES.SPECIAL_OFFER,
	NEGOCIATED_TYPES.ACCOMMODATION,
	NEGOCIATED_TYPES.BOARD,
	NEGOCIATED_TYPES.CAR_RENTAL,
	NEGOCIATED_TYPES.TRANSFER,
	NEGOCIATED_TYPES.FLIGHT,
];

const HolidaySummary = ({
	isSDP,
	booking,
	ficheProduit,
	showProductPicture,
	showFlightDetails,
	showTimer,
	showBookingBanner,
	hasSdpSpecialOffer,
	specialOfferInfos,
	hideQuoteOptions,
}) => {
	const accommodation = booking.accommodation || {};
	const board = booking.board || {};
	const flight = booking.flight || {};
	const transfer = booking.transfer || {};
	const departureDate = booking.departureDate;

	const intl = useIntl();
	const flightLabelKey = getFlightNegotiatedLabelKey(flight);
	const flightNegociatedLabel = intl.formatMessage({ id: flightLabelKey });

	const rating = get(ficheProduit, "tripAdvisor.rating");

	const { resolution } = useContext(AppGlobalsContext);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const photo = ficheProduit.photos && ficheProduit.photos[0];

	// pour pp, les negotiated sont par type d'offre alors que pour sdp, non
	const negotiatedList = get(booking, "offer.negotiated") || ficheProduit.negotiated || [];

	const negotiatedListToDisplay =
		(negotiatedList &&
			negotiatedList.filter(negotiated => {
				return negotiated.type && !NEGOS_TO_EXCLUDE.includes(negotiated.type);
			})) ||
		[];

	return (
		<div className="holiday-summary">
			{showBookingBanner && <BookingBannerPlaceholderContainer ignorePagecheck />}

			<div className="holiday-summary__details">
				{showProductPicture && photo && (
					<div className="holiday-summary__picture">
						{isMobile ? (
							<BackgroundProgressiveImg
								sizes={IMG_SIZES_MOBILE}
								src={photo.url}
								alt={photo.alt}
								thumbnail={photo.thumbnail}
								backgroundPosition={"center"}
							/>
						) : (
							<ProgressiveImg
								sizes={IMG_SIZES_DESKTOP}
								src={photo.url}
								alt={photo.alt}
								resolution={resolution}
								thumbnail={photo.thumbnail}
							/>
						)}
						{isMobile && hasSdpSpecialOffer && (
							<div className="holiday-summary__badge">
								<SpecialOfferBadge
									background={get(specialOfferInfos, "ui.background")}
									title={specialOfferInfos.title}
									pictoUrl={get(specialOfferInfos, "ui.pictoUrl")}
								/>
							</div>
						)}
					</div>
				)}

				{showTimer && (
					<div className="holiday-summary__time-left">
						<Timer
							day={get(ficheProduit, "expiration.day")}
							hour={get(ficheProduit, "expiration.hour")}
							suffix={<FormattedMessage id="quotation.timer.left.label" />}
						/>
					</div>
				)}

				<div className="holiday-summary__title" data-cy="detail-resort">
					<FormattedMessage id="booking.holiday.summary.to.title" />
				</div>

				<ul className="holiday-summary__list">
					<li className="holiday-summary__list-item" data-cy="detail-location">
						<IconLocation />
						{ficheProduit.region}, {ficheProduit.resort}
					</li>

					{departureDate && (
						<Collapse
							className="holiday-summary__collapse"
							expandIcon={({ isActive }) => {
								return (
									<IconArrowDown
										style={{
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
									/>
								);
							}}
						>
							<Collapse.Panel
								headerClass="holiday-summary__collapse-header"
								header={
									<li className="holiday-summary__list-item">
										<IconCalendar />
										<TravelDatesContainer />
									</li>
								}
							>
								<div className="holiday-summary__collapse-content">
									<TravelDurationDatesContainer />
								</div>
							</Collapse.Panel>
						</Collapse>
					)}

					<li className="holiday-summary__list-item" data-cy="detail-travellers">
						<i className="icon icon--travellers" />
						{isSDP ? (
							<OccupanciesDisplayLabel occupancies={booking.occupancies} />
						) : (
							<PassengersCounterDisplay
								adults={booking.adults}
								children={booking.children}
								infants={booking.infants}
							/>
						)}
					</li>

					<li className="holiday-summary__list-item" data-cy="detail-product-name">
						<i className="icon icon--hotel" />
						<div className="holiday-summary__product-name">
							<ProductNameWithCategory
								name={ficheProduit.name}
								category={ficheProduit.category}
							/>
							{rating > 0 && <TripAdvisor rating={rating} />}
						</div>
					</li>

					{!hideQuoteOptions && accommodation.items && accommodation.items.length > 0 && (
						<li className="holiday-summary__list-item" data-cy="detail-accommodation">
							<IconNegoBed />
							<div>
								{accommodation.items.map((item, index) => {
									return <div key={index}>1 x {item.label}</div>;
								})}
							</div>
						</li>
					)}

					{!hideQuoteOptions && board.label && (
						<li className="holiday-summary__list-item" data-cy="detail-board">
							<IconNegoDinner />
							{board.label}
						</li>
					)}

					{!hideQuoteOptions && showFlightDetails && (
						<Collapse
							className="holiday-summary__collapse"
							expandIcon={({ isActive }) => {
								return (
									<IconArrowDown
										style={{
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
									/>
								);
							}}
						>
							<Collapse.Panel
								headerClass="holiday-summary__collapse-header"
								header={
									<li
										className="holiday-summary__list-item"
										data-cy="detail-flight"
									>
										<IconNegoPlane />
										{flightNegociatedLabel}
									</li>
								}
							>
								<div className="holiday-summary__collapse-content">
									<MyFlightDetail flight={booking.flight} />
								</div>
							</Collapse.Panel>
						</Collapse>
					)}

					{!hideQuoteOptions && transfer.vehicule && (
						<li className="holiday-summary__list-item" data-cy="detail-transfer">
							<IconNegoTransfer />
							{transfer.vehicule}
						</li>
					)}

					{!hideQuoteOptions && negotiatedListToDisplay.length > 0 && (
						<Collapse
							className="holiday-summary__collapse"
							expandIcon={({ isActive }) => {
								return (
									negotiatedListToDisplay.length > 1 && (
										<IconArrowDown
											style={{
												transform: `rotate(${isActive ? 180 : 0}deg)`,
											}}
										/>
									)
								);
							}}
						>
							<Collapse.Panel
								headerClass="holiday-summary__collapse-header"
								header={
									<li
										className="holiday-summary__list-item"
										data-cy="detail-negotiated"
									>
										<NegociatedItem
											label={negotiatedListToDisplay[0].label}
											enableLineClamp={false}
										/>
									</li>
								}
							>
								<div className="holiday-summary__collapse-content holiday-summary__collapse-content--negotiated">
									{negotiatedListToDisplay
										.slice(1, negotiatedListToDisplay.length)
										.map((negotiated, index) => {
											return (
												<li
													key={index}
													className="holiday-summary__list-item"
												>
													<NegociatedItem label={negotiated.label} />
												</li>
											);
										})}
								</div>
							</Collapse.Panel>
						</Collapse>
					)}
				</ul>
			</div>
		</div>
	);
};

HolidaySummary.propTypes = {
	isSDP: PropTypes.bool,
	showFlightDetails: PropTypes.bool,
	showProductPicture: PropTypes.bool,
	booking: PropTypes.object,
	ficheProduit: PropTypes.object,
	showTimer: PropTypes.bool,
	hasSdpSpecialOffer: PropTypes.bool,
	showBookingBanner: PropTypes.bool,
	specialOfferInfos: PropTypes.object,
	hideQuoteOptions: PropTypes.bool,
};

HolidaySummary.defaultProps = {
	booking: {},
	ficheProduit: {},
	specialOfferInfos: {},
};

export default React.memo(HolidaySummary);
